import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Container, FlaskConical, Sprout } from "lucide-react";

import { useGrows } from "@/lib/api/grows";
import {
  usePendingActionsAcrossGrows,
  useResolvePendingAction,
} from "@/lib/api/pending-actions";
import { useReservoirs } from "@/lib/api/reservoirs";
import { useMe } from "@/lib/api/me";
import type { Grow, PendingAction, Urgency } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ErrorState } from "@/components/error-state";
import { UrgencyBadge } from "@/components/urgency-badge";
import { GrowCard } from "@/components/dashboard/grow-card";
import { CaptureSelectionProvider } from "@/lib/capture/selection";

// Dashboard is the landing page: a per-grow card for every active grow
// (current chemistry + deltas from ideal + next action + refill-history
// sparkline), fronted by a small count summary. Each card fetches its own
// latest refill session and pending actions, so the page composes cleanly
// without a bespoke aggregate endpoint.

export function DashboardPage() {
  const me = useMe();
  const grows = useGrows();
  const reservoirs = useReservoirs();

  if (grows.error)
    return <ErrorState error={grows.error} retry={grows.refetch} />;

  const items = grows.data?.items ?? [];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        {me.data && (
          <p className="text-sm text-muted-foreground">
            tenant <code className="font-mono">{me.data.tenant_id}</code> · user{" "}
            <code className="font-mono">{me.data.user_id}</code>
          </p>
        )}
      </header>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          icon={<Sprout className="size-5" aria-hidden />}
          label="Active grows"
          value={grows.isLoading ? "…" : String(items.length)}
          to="/grows"
        />
        <StatCard
          icon={<Container className="size-5" aria-hidden />}
          label="Reservoirs"
          value={countValue(
            reservoirs.isLoading,
            reservoirs.error,
            reservoirs.data?.items.length,
          )}
          to="/reservoirs"
        />
        <StatCard
          icon={<FlaskConical className="size-5" aria-hidden />}
          label="Chemistry profile"
          value="View"
          to="/chemistry-profile"
        />
      </section>

      {/* Grows is the dashboard's primary content — one rich tile per active
          grow — so it leads the page; the cross-grow Next steps roll-up follows
          as a focused action list. */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <SectionHeading title="Grows" count={items.length} />
          <Link to="/grows/new" className="text-sm underline">
            New grow
          </Link>
        </div>

        {grows.isLoading && (
          <p className="text-sm text-muted-foreground">Loading grows…</p>
        )}

        {!grows.isLoading && items.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No grows yet.{" "}
            <Link to="/grows/new" className="underline">
              Create one
            </Link>
            .
          </p>
        )}

        {/* One selection scope for the whole grid: clicking/expanding a card
            arms it, and `c` captures into the selected card only. */}
        <CaptureSelectionProvider>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((g) => (
              <GrowCard key={g.id} grow={g} />
            ))}
          </div>
        </CaptureSelectionProvider>
      </section>

      {items.length > 0 && <NextSteps grows={items} />}
    </div>
  );
}

// SectionHeading renders an <h2> with an optional muted count chip, so the
// dashboard's section titles ("Grows", "Next steps") read consistently and
// stay glanceable at a distance.
function SectionHeading({ title, count }: { title: string; count?: number }) {
  return (
    <h2 className="flex items-center gap-2 text-lg font-medium">
      {title}
      {count !== undefined && count > 0 && (
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
          {count}
        </span>
      )}
    </h2>
  );
}

const URGENCY_RANK: Record<Urgency, number> = {
  HIGH: 0,
  MED: 1,
  ROUTINE: 2,
  PARKED: 3,
};

// NextSteps rolls up the most urgent open pending actions across every grow
// into one "what do I do next" list at the top of the dashboard. It reuses the
// per-grow pending-action cache (shared with the grow cards), so it adds no
// extra requests. Each row links to the grow so you can act on it.
function NextSteps({ grows }: { grows: Grow[] }) {
  const { items, isLoading } = usePendingActionsAcrossGrows(
    grows.map((g) => g.id),
  );
  const growById = new Map(grows.map((g) => [g.id, g]));
  const open = items.filter((a) => !a.dismissed_at);
  const top = open
    .slice()
    .sort((a, b) => URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency])
    .slice(0, 5);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionHeading title="Next steps" count={open.length} />
        <Link to="/pending-actions" className="text-sm underline">
          All pending
        </Link>
      </div>
      {/* Show whatever's already loaded rather than blocking the whole list on
          the slowest grow's query; only fall back to Loading/empty when there
          is genuinely nothing to show yet. */}
      {top.length === 0 ? (
        isLoading ? (
          <p className="text-sm text-muted-foreground">Loading next steps…</p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Nothing pending — you're all caught up. ✨
          </p>
        )
      ) : (
        <ul className="divide-y rounded-md border">
          {top.map((a) => (
            <NextStepRow key={a.id} action={a} grow={growById.get(a.grow_id)} />
          ))}
        </ul>
      )}
    </section>
  );
}

// NextStepRow surfaces the grow prominently (its own chip, not buried in muted
// text) plus the urgency badge + title, and adds compact inline "Done" / "Skip"
// affordances so the grower can resolve it without opening the grow.
//
// Done and Skip now record a REAL resolution via the `:resolve` endpoint
// ("completed" vs "skipped" with an optional reason). The server writes an
// audit row against the owning grow, so the outcome lands in the grow's
// activity log. On resolve the hook invalidates the per-grow cache, so the row
// disappears.
function NextStepRow({
  action,
  grow,
}: {
  action: PendingAction;
  grow: Grow | undefined;
}) {
  const resolve = useResolvePendingAction(action.id, action.grow_id);

  function onSkip() {
    const reason =
      window.prompt("Reason for skipping? (optional)") || undefined;
    resolve.mutate({ resolution: "skipped", reason });
  }

  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <Link
          to="/grows/$growId"
          params={{ growId: action.grow_id }}
          className="-m-1 min-w-0 flex-1 rounded p-1 hover:bg-accent/50"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
            🌱 {grow ? `${grow.species} · ${grow.system_type}` : `grow ${action.grow_id.slice(-6)}`}
          </span>
          <div className="mt-1 flex items-start gap-2">
            <UrgencyBadge urgency={action.urgency} className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium">{action.title}</p>
              {action.rationale && (
                <p className="line-clamp-1 text-xs text-muted-foreground">
                  {action.rationale}
                </p>
              )}
            </div>
          </div>
        </Link>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ resolution: "completed" })}
          >
            Done
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={resolve.isPending}
            onClick={onSkip}
          >
            Skip
          </Button>
        </div>
      </div>
    </li>
  );
}

function countValue(
  isLoading: boolean,
  error: unknown,
  count: number | undefined,
): string {
  if (isLoading) return "…";
  if (error) return "—";
  return String(count ?? 0);
}

// StatCard is the compact summary tile at the top of the dashboard: an icon
// badge, a muted label, the value, and a hover chevron that telegraphs the
// whole card is a link. Tighter than a full Card header/content stack, and the
// chevron keeps the non-numeric "Chemistry profile" tile reading as navigation
// rather than a broken stat.
function StatCard({
  icon,
  label,
  value,
  to,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  to: string;
}) {
  return (
    <Link to={to} className="group block">
      <Card className="gap-0 py-0 transition-colors hover:bg-accent/50">
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            {icon}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-muted-foreground">{label}</p>
            <p className="text-xl font-semibold leading-tight tabular-nums">
              {value}
            </p>
          </div>
          <ChevronRight
            className="size-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        </div>
      </Card>
    </Link>
  );
}
